import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Container,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  Button,
} from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { IoIosArrowUp } from "react-icons/io";
import resetBtn from "../../assets/images/reset-arrow.svg";
import downloadBtn from "../../assets/images/filter-download-btn.svg";
import {
  getExpediteVisitConfig,
  deleteExpediteVisitOption,
  reorderExpediteVisitOptions,
  updateExpediteVisitOption,
} from "../../actions/expediteVisitAction";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import FullPageLoader from "../../components/FullPageLoader/FullPageLoader";
import SweetAlert from "react-bootstrap-sweetalert";
import { toast } from "react-toastify";
import _debounce from "lodash/debounce";
import DataTable, { createTheme } from "react-data-table-component";

createTheme(
  "solarized",
  {
    text: {
      primary: "#000",
      secondary: "#000",
    },
    background: {
      default: "#fff",
    },
    context: {
      background: "#cb4b16",
      text: "#FFFFFF",
    },
    divider: {
      default: "#DFE0EB",
    },
    action: {
      button: "rgba(0,0,0,.54)",
      hover: "rgba(0,0,0,.08)",
      disabled: "rgba(0,0,0,.12)",
    },
  },
  "dark"
);

const ExpediteVisit = () => {
  const [config, setConfig] = useState({
    title: "",
    subtitle: "",
    options: [],
  });
  const [loader, setLoader] = useState(false);
  const { admin } = useSelector((state) => state.admin);
  const currentUserRole = admin.role;
  const [getDelete, setDelete] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [filterText, setFilterText] = useState("");
  const [debouncedText, setDebouncedText] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [orderValues, setOrderValues] = useState({});
  const [isFilterOpen, setIsFilterOpen] = useState(true);

  const hideModel = () => {
    setDelete(false);
  };

  const deleteOption = async () => {
    setDelete(false);
    setLoader(true);
    let res = await deleteExpediteVisitOption(deleteId);
    if (res.data.success) {
      toast.success(res.data.message);
      getExpediteVisitData();
    }
    setLoader(false);
  };

  const showModel = (id) => {
    setDeleteId(id);
    setDelete(true);
  };

  const getExpediteVisitData = async () => {
    setLoader(true);
    let res = await getExpediteVisitConfig();
    if (res?.data?.data) {
      setConfig(res.data.data);
      setFilteredItems(res.data.data.options || []);
      // Initialize order values
      const orderMap = {};
      (res.data.data.options || []).forEach((opt) => {
        orderMap[opt._id || opt.id] = opt.order || 0;
      });
      setOrderValues(orderMap);
    }
    setLoader(false);
  };

  const filterDatatable = (text) => {
    setFilterText(text);
  };

  const filterSearchDatatable = useCallback((text) => {
    if (!text || !text.trim()) {
      setFilteredItems(config.options || []);
      return;
    }

    const searchLower = text.toLowerCase();
    const filtered = (config.options || []).filter((option) => {
      return (
        option.title?.toLowerCase().includes(searchLower) ||
        option.timeFrame?.toLowerCase().includes(searchLower) ||
        option.description?.toLowerCase().includes(searchLower) ||
        option.price?.toString().includes(searchLower)
      );
    });
    setFilteredItems(filtered);
  }, [config.options]);

  const resetFilter = () => {
    setFilterText("");
    setFilteredItems(config.options || []);
  };

  const toggleFilterAccordion = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const debouncedFilterDatatable = useCallback(
    _debounce((text) => {
      setDebouncedText(text.trim());
    }, 500),
    []
  );

  useEffect(() => {
    debouncedFilterDatatable(filterText);
    return () => debouncedFilterDatatable.cancel();
  }, [filterText, debouncedFilterDatatable]);

  useEffect(() => {
    filterSearchDatatable(debouncedText);
  }, [debouncedText, filterSearchDatatable]);

  useEffect(() => {
    if (config.options) {
      setFilteredItems([...config.options]);
    }
  }, [config.options]);

  useEffect(() => {
    getExpediteVisitData();
  }, []);

  const caseInsensitiveSort = (rowA, rowB, columnId) => {
    const getProperty = (row, columnId) => {
      switch (columnId) {
        case "Title":
          return row?.title?.toLowerCase() || "";
        case "Time Frame":
          return row?.timeFrame?.toLowerCase() || "";
        case "Price":
          return row?.price || 0;
        case "Order Number":
          return row?.order || 0;
        default:
          return "";
      }
    };

    const a = getProperty(rowA, columnId);
    const b = getProperty(rowB, columnId);

    if (a > b) {
      return 1;
    }
    if (b > a) {
      return -1;
    }
    return 0;
  };

  const handleOrderChange = (optionId, newOrder) => {
    if (newOrder === "" || newOrder === null || newOrder === undefined) {
      setOrderValues({ ...orderValues, [optionId]: "" });
      return;
    }

    const orderNum = parseInt(newOrder);
    if (isNaN(orderNum) || orderNum < 1) {
      return;
    }

    setOrderValues({ ...orderValues, [optionId]: orderNum });
  };

  const handleOrderBlur = async (optionId, newOrder) => {
    if (!newOrder || newOrder === "" || newOrder === null || newOrder === undefined) {
      setEditingOrderId(null);
      getExpediteVisitData();
      return;
    }

    const orderNum = parseInt(newOrder);
    if (isNaN(orderNum) || orderNum < 1) {
      toast.error("Order number must be a positive integer");
      setEditingOrderId(null);
      getExpediteVisitData();
      return;
    }

    // Check for duplicate order numbers (excluding current option)
    const currentOption = config.options.find(
      (opt) => (opt._id && opt._id.toString() === optionId.toString()) || (opt.id && opt.id.toString() === optionId.toString())
    );

    const hasDuplicate = config.options.some(
      (opt) => {
        const optId = opt._id || opt.id;
        return (
          optId.toString() !== optionId.toString() &&
          opt.order === orderNum
        );
      }
    );

    if (hasDuplicate) {
      toast.error("This order number is already in use. Please choose a different number.");
      setEditingOrderId(null);
      getExpediteVisitData();
      return;
    }

    // If order number hasn't changed, just close the editor
    if (currentOption && currentOption.order === orderNum) {
      setEditingOrderId(null);
      return;
    }

    setEditingOrderId(null);
    setLoader(true);

    // Update the order number
    let res = await updateExpediteVisitOption(optionId, { order: orderNum });
    if (res?.data?.success) {
      toast.success("Order number updated successfully");
      getExpediteVisitData();
    } else {
      toast.error(res?.data?.message || "Failed to update order number");
      getExpediteVisitData();
    }
    setLoader(false);
  };

  const columns = [
    {
      name: "ID",
      selector: (row, index) => index + 1,
      sortable: false,
      width: "80px",
      button: true,
    },
    {
      name: "Order Number",
      cell: (row) => {
        const optionId = row._id || row.id;
        const isEditing = editingOrderId === optionId;
        return isEditing ? (
          <Form.Control
            type="number"
            min="1"
            className="order-number-input"
            value={orderValues[optionId] || row.order || ""}
            onChange={(e) => handleOrderChange(optionId, e.target.value)}
            onBlur={(e) => handleOrderBlur(optionId, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleOrderBlur(optionId, e.target.value);
              }
              if (e.key === "Escape") {
                setEditingOrderId(null);
                getExpediteVisitData();
              }
            }}
            autoFocus
          />
        ) : (
          <div
            className="order-number-display"
            onClick={() => setEditingOrderId(optionId)}
          >
            <span className="order-number-value">{row.order || 0}</span>
            <div className="order-edit-icon-wrapper">
              <FaEdit className="order-edit-icon" size={14} />
            </div>
          </div>
        );
      },
      sortable: true,
      sortFunction: (a, b) => caseInsensitiveSort(a, b, "Order Number"),
      width: "200px",
      ignoreRowClick: true,
      button: true,
    },
    {
      name: "Title",
      selector: (row) => row?.title,
      sortable: true,
      sortFunction: (a, b) => caseInsensitiveSort(a, b, "Title"),
      button: true,
      width: "380px",
    },
    {
      name: "Time Frame",
      selector: (row) => {
        if (row?.timeFrameFrom !== undefined && row?.timeFrameTo !== undefined) {
          const from = row.timeFrameFrom;
          const to = row.timeFrameTo;
          // Convert to hours if >= 60 minutes
          if (from >= 60 || to >= 60) {
            const fromHours = from / 60;
            const toHours = to / 60;
            if (fromHours % 1 === 0 && toHours % 1 === 0) {
              return `${fromHours}-${toHours} ${fromHours === 1 ? 'hour' : 'hours'}`;
            } else {
              const fromRounded = Math.round(fromHours * 10) / 10;
              const toRounded = Math.round(toHours * 10) / 10;
              return `${fromRounded}-${toRounded} hours`;
            }
          } else {
            return `${from}-${to} minutes`;
          }
        }
        return row?.timeFrame || "N/A";
      },
      sortable: true,
      sortFunction: (a, b) => {
        const aValue = a?.timeFrameFrom || 0;
        const bValue = b?.timeFrameFrom || 0;
        return aValue - bValue;
      },
      width: "280px",
      button: true,
    },
    {
      name: "Price",
      selector: (row) => `$${row?.price || 0}`,
      sortable: true,
      sortFunction: (a, b) => caseInsensitiveSort(a, b, "Price"),
      width: "280px",
      button: true,
    },
    {
      name: "Actions",
      selector: (row) => (
        <div className="td-actions table-actions-btns">
          <ul className="list-unstyled mb-0 d-flex justify-content-center">
            {currentUserRole?.editExpediteVisitOption ? (
              <li className="d-inline-block align-top">
                <OverlayTrigger
                  overlay={<Tooltip id="tooltip-edit">Edit</Tooltip>}
                  placement="left"
                >
                  <Link
                    to={`/expedite-visit/option/edit/${row._id || row.id}`}
                    type="button"
                    className="btn-link btn-icon btn btn-success"
                  >
                    <FaEdit />
                  </Link>
                </OverlayTrigger>
              </li>
            ) : (
              <li className="d-inline-block align-top">
                <OverlayTrigger
                  overlay={<Tooltip id="tooltip-edit">Edit</Tooltip>}
                  placement="left"
                >
                  <Link
                    to={""}
                    type="button"
                    className="btn-link btn-icon btn btn-success disabled"
                    disabled
                  >
                    <FaEdit />
                  </Link>
                </OverlayTrigger>
              </li>
            )}
            {currentUserRole?.deleteExpediteVisitOption ? (
              <li className="d-inline-block align-top">
                <OverlayTrigger
                  overlay={<Tooltip id="tooltip-delete">Delete</Tooltip>}
                  placement="left"
                >
                  <button
                    onClick={() => showModel(row._id || row.id)}
                    type="button"
                    className="btn-link btn-icon btn btn-danger"
                  >
                    <FaTrash />
                  </button>
                </OverlayTrigger>
              </li>
            ) : (
              <li className="d-inline-block align-top">
                <OverlayTrigger
                  overlay={<Tooltip id="tooltip-delete">Delete</Tooltip>}
                  placement="left"
                >
                  <button
                    type="button"
                    className="btn-link btn-icon btn btn-danger disabled"
                    disabled
                  >
                    <FaTrash />
                  </button>
                </OverlayTrigger>
              </li>
            )}
          </ul>
        </div>
      ),
      sortable: false,
      width: "280px",
      button: true,
    },
  ];

  return (
    <>
      {getDelete ? (
        <SweetAlert
          warning
          showCloseButton
          showCancel
          confirmBtnText="Yes"
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="light"
          title="Are you sure you want to delete?"
          onConfirm={deleteOption}
          onCancel={hideModel}
          focusCancelBtn
          customClass="del-sweet-alert"
        >
          You will not be able to recover this data!
        </SweetAlert>
      ) : null}
      {loader ? (
        <FullPageLoader />
      ) : (
        <div className="pt-2 pt-md-3 job-form-admin-table">
          <Container fluid>
            <Row className="expedite-visit-wraper ">
              <Col md="12">
                <Card className="filter-card card">
                  <Card.Header 
                    style={{ cursor: "pointer" }}
                    onClick={toggleFilterAccordion}
                  >
                    <div className="d-flex align-items-center justify-content-between table-head">
                      <Card.Title className="text-white" as="h4">
                        Filter
                      </Card.Title>
                      <div 
                        style={{ 
                          transform: isFilterOpen ? "rotate(0deg)" : "rotate(180deg)",
                          transition: "transform 0.3s ease"
                        }}
                      >
                        <IoIosArrowUp />
                      </div>
                    </div>
                     
                  </Card.Header>
                  {isFilterOpen && (
                  <Card.Body>
                    <Row>
                      <Col xl={4} sm={6} className="search-wrap">
                        <Form.Group>
                          <Form.Label className="d-block mb-2 text-white">
                            Search with title...
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="search"
                            placeholder="Search"
                            value={filterText}
                            onChange={(e) => filterDatatable(e.target.value)}
                          ></Form.Control>
                        </Form.Group>
                      </Col>
                      <Col xl={4} md={6}>
                        <Form.Group>
                          <label className="d-none d-sm-block mb-2 form-label">
                            &nbsp;
                          </label>
                          <div className="d-flex filter-btns-holder">
                            <button
                              type="button"
                              className="outline-button filter-reset-btn"
                              onClick={() => resetFilter()}
                            >
                              <img src={resetBtn} className="img-fluid mr-3" alt="logo"/>
                              Reset
                            </button>
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                  )}
                </Card>

                <div className="datatable-title card d-flex flex-row justify-content-between align-items-center mb-0">
                  <h4 className="card-title">Expedite Visit Options</h4>
                  {currentUserRole?.addExpediteVisitOption ? (
                    <div className="btn-add">
                      <Link
                        className="float-sm-right btn-filled d-flex align-items-center"
                        to="/expedite-visit/option/add"
                      >
                        <span className="add-icon mr-2">
                          <FaPlus />
                        </span>{" "}
                        Add Option
                      </Link>
                    </div>
                  ) : (
                    <div className="btn-add">
                      <Button
                        className="float-sm-right btn-filled d-flex align-items-center disabled"
                        disabled
                      >
                        <span className="add-icon mr-2">
                          <FaPlus />
                        </span>{" "}
                        Add Option
                      </Button>
                    </div>
                  )}
                </div>
                  <div className="custom-datatable-pagination">
                <DataTable
                  columns={columns}
                  data={filteredItems}
                  theme="solarized"
                  pagination
                  className="react-datatable"
                />
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      )}
    </>
  );
};

export default ExpediteVisit;


